import { trackButtonClicked, useManualExposure } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import { TierProFilled } from '@heidi/design-system/icons';
import { Progress } from '@heidi/design-system/progress';
import {
  TypographyFootNote,
  TypographyH3,
  TypographyP,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenTrialExtensionModalAtom,
  trialExtensionModalOpenAtom,
} from '../../data';
import {
  getModalFooter,
  getModalSubtitle,
  getModalTitle,
  getTrialExtensionProgress,
} from '../hooks/trialExtensionProgress';
import { getCommerceBillingUiPorts } from '../settings/billing/billing-ui-ports';
import { getCommerceTrialsUiPorts } from '../trials-and-upgrades/trials-ui-ports';
import { TrialExtensionTaskRow } from './trial-extension/TrialExtensionTaskRow';

/**
 * Practice-tier-only gamify trial extension modal (old design).
 * For Pro/Clinician tier use GamifyTrialExtensionTierModal instead.
 */
export function GamifyTrialExtensionPracticeModal() {
  const commerceBillingUiPorts = getCommerceBillingUiPorts();
  const commerceTrialsUiPorts = getCommerceTrialsUiPorts();
  const TrialExtensionModalWrapper = commerceTrialsUiPorts.TrialExtensionModalWrapper;
  const [isOpen, setIsOpen] = useAtom(trialExtensionModalOpenAtom);
  const setEarnDaysNavPermanentlyDismissed = useSetAtom(
    earnDaysNavPermanentlyDismissedAtom
  );
  const setHasSeenTrialExtensionModal = useSetAtom(hasSeenTrialExtensionModalAtom);
  const { data, isLoading } = commerceBillingUiPorts.useQueryGamifyTrialExtension(isOpen);
  const { t } = useTranslations();
  const intl = useIntl();
  const { togglePracticeUpgradeModal } = commerceBillingUiPorts.usePaywallStates();

  useManualExposure({
    flagKey: 'release-gamify-trial-length',
    isEnabled: true,
    isVisible: isOpen && !!data?.eligible,
  });

  useEffect(() => {
    if (isOpen) setHasSeenTrialExtensionModal(true);
  }, [isOpen, setHasSeenTrialExtensionModal]);

  const { handleClose, handleCloseAutoFocus, getTaskHandler, handleTaskClick } =
    commerceTrialsUiPorts.useTrialExtensionModalHandlers(
      isOpen,
      data ?? undefined,
      setIsOpen
    );

  const hasEligibleOrCompletedData = data && (data.eligible || data.trial_extended);
  if (!hasEligibleOrCompletedData) {
    return null;
  }

  const progress = getTrialExtensionProgress(data);
  const { allTasksComplete, progressPercent, completedCount, totalCount } = progress;

  const tierLabel = t('trialExtension.tierNamePractice');

  const title = getModalTitle(
    progress,
    t,
    t('trialExtension.earnMoreDays', { days: data.trial_extension_days })
  );
  const subtitle = getModalSubtitle(progress, t, intl, tierLabel);
  const footerText = getModalFooter(progress, t, tierLabel);

  const handleKeepPracticeAfterTrial = () => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionModal,
      action: ButtonAction.TrialExtensionUpgradeToPractice,
      label: 'Upgrade to Practice',
    });
    handleClose();
    togglePracticeUpgradeModal(true);
  };

  return (
    <TrialExtensionModalWrapper
      isOpen={isOpen}
      onClose={() => {
        if (allTasksComplete) setEarnDaysNavPermanentlyDismissed(true);
        handleClose();
      }}
      onCloseAutoFocus={handleCloseAutoFocus}
      hideUpgradeBanner
    >
      <div className="flex flex-col">
        <TypographyH3>{title}</TypographyH3>
        <TypographyP className="mt-1 text-text-secondary">{subtitle}</TypographyP>
        <div className="mt-1 flex items-center gap-3 pt-6">
          <Progress
            value={allTasksComplete ? 100 : progressPercent}
            className="h-2 flex-1"
          />
          <span className="text-sm text-text-secondary">
            {t('trialExtension.completed', {
              completed: completedCount,
              total: totalCount,
            })}
          </span>
        </div>
      </div>

      <div className="flex flex-col divide-y divide-border">
        {isLoading ? (
          <div className="py-8 text-center text-text-secondary">
            {t('trialExtension.loading')}
          </div>
        ) : (
          data.tasks?.map((task) => (
            <TrialExtensionTaskRow
              key={task.id}
              task={task}
              variant="practice"
              onAction={getTaskHandler(task.id)}
              onTaskClick={handleTaskClick}
            />
          ))
        )}
      </div>

      <div className="flex flex-col gap-2">
        {allTasksComplete && !commerceBillingUiPorts.isAppleCompliantBuild() && (
          <Button
            size="lg"
            variant="pro"
            className="w-full"
            onClick={handleKeepPracticeAfterTrial}
          >
            <TierProFilled size={16} />
            {t('trialExtension.keepPlanAfterTrial', {
              plan: t('trialExtension.tierNamePractice'),
            })}
          </Button>
        )}
        <TypographyFootNote className="text-center text-text-secondary">
          {footerText}
        </TypographyFootNote>
      </div>
    </TrialExtensionModalWrapper>
  );
}
